import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LogOut, Users, DollarSign, Clock, CheckCircle, 
  Mail, Search, ChevronDown, Eye, Send, RefreshCw,
  BarChart3, Globe, Inbox, SendHorizontal, TrendingUp,
  Reply, PenSquare, Timer, Paperclip, X, Download, RotateCw,
  ArrowUpDown, ArrowUp, ArrowDown, Star, Trash2, MailOpen, MailCheck,
  Forward, CheckSquare, Square, Trash, ArchiveRestore, AlertTriangle, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  getAdminBookings, getAdminStats, updateBookingStatus, sendEmail,
  getSentEmails, getReceivedEmails, markEmailRead, markEmailUnread,
  toggleStarReceived, toggleStarSent, deleteReceivedEmail, deleteSentEmail,
  bulkDeleteReceived, bulkDeleteSent, bulkMarkRead, retryAttachments,
  getSalesAnalytics, getTrafficAnalytics,
  deleteBooking, getTrashedBookings, restoreBooking, permanentDeleteBooking,
  getTrashedEmails, restoreEmail, permanentDeleteEmail, adminCreateBooking,
  getServices
} from '../lib/api';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const LOGO_URL = "/pt-logo.png?v=2";

const SESSION_TIMEOUT = 30 * 60 * 1000;

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const PAYMENT_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800'
};

const TABS = [
  { id: 'bookings', label: 'Bookings', icon: Users },
  { id: 'emails', label: 'Emails', icon: Mail },
  { id: 'analytics', label: 'Sales Analytics', icon: BarChart3 },
  { id: 'traffic', label: 'Site Traffic', icon: Globe },
  { id: 'trash', label: 'Trash', icon: Trash },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshingTab, setRefreshingTab] = useState(null);

  const withSpin = useCallback(async (tabKey, fn) => {
    setRefreshingTab(tabKey);
    const minDelay = new Promise(r => setTimeout(r, 600));
    try { await Promise.all([fn(), minDelay]); } finally { setRefreshingTab(null); }
  }, []);

  // Email states
  const [sentEmails, setSentEmails] = useState([]);
  const [receivedEmails, setReceivedEmails] = useState([]);
  const [emailSubTab, setEmailSubTab] = useState('received');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());

  // Analytics states
  const [salesData, setSalesData] = useState(null);
  const [salesPeriod, setSalesPeriod] = useState('daily');
  
  // Traffic states
  const [trafficData, setTrafficData] = useState(null);
  const [trafficPeriod, setTrafficPeriod] = useState('daily');

  // Trash states
  const [trashedBookings, setTrashedBookings] = useState([]);
  const [trashedEmails, setTrashedEmails] = useState([]);
  const [deleteConfirmBooking, setDeleteConfirmBooking] = useState(null);

  // Add Booking Dialog states
  const [isAddBookingOpen, setIsAddBookingOpen] = useState(false);
  const [addBookingData, setAddBookingData] = useState({
    service_type: 'round_trip',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    lakewood_street: '',
    lakewood_city: 'Lakewood',
    lakewood_state: 'NJ',
    lakewood_zip: '08701',
    orlando_street: '',
    orlando_city: 'Orlando',
    orlando_state: 'FL',
    orlando_zip: '32801',
    payment_method: 'stripe',
    payment_status: 'pending',
    status: 'pending',
    notes: ''
  });
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);

  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetSessionTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_email');
      navigate('/admin');
    }, SESSION_TIMEOUT);
  }, [navigate]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => resetSessionTimeout();
    events.forEach(event => window.addEventListener(event, handleActivity));
    resetSessionTimeout();
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetSessionTimeout]);

  const loadTrashedBookings = useCallback(async () => {
    try {
      const res = await getTrashedBookings();
      setTrashedBookings(res.data.bookings || []);
    } catch (err) { console.error("Data load error:", err); }
  }, []);

  const loadTrashedEmails = useCallback(async () => {
    try {
      const res = await getTrashedEmails();
      setTrashedEmails(res.data.emails || []);
    } catch (err) { console.error("Data load error:", err); }
  }, []);

  const handleDeleteBooking = async (booking) => {
    try {
      await deleteBooking(booking.id);
      toast.success('Booking moved to trash');
      setDeleteConfirmBooking(null);
      loadData();
      loadTrashedBookings();
    } catch (err) {
      toast.error('Failed to delete booking');
    }
  };

  const handleRestoreBooking = async (bookingId) => {
    try {
      await restoreBooking(bookingId);
      toast.success('Booking restored');
      loadData();
      loadTrashedBookings();
    } catch (err) {
      toast.error('Failed to restore booking');
    }
  };

  const handlePermanentDelete = async (bookingId) => {
    try {
      await permanentDeleteBooking(bookingId);
      toast.success('Booking permanently deleted');
      loadTrashedBookings();
    } catch (err) {
      toast.error('Failed to permanently delete');
    }
  };

  const handleRestoreEmail = async (emailId, type) => {
    try {
      await restoreEmail(emailId, type);
      toast.success('Email restored');
      loadTrashedEmails();
    } catch (err) {
      toast.error('Failed to restore email');
    }
  };

  const handlePermanentDeleteEmail = async (emailId, type) => {
    try {
      await permanentDeleteEmail(emailId, type);
      toast.success('Email permanently deleted');
      loadTrashedEmails();
    } catch (err) {
      toast.error('Failed to permanently delete email');
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        getAdminBookings(),
        getAdminStats()
      ]);
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    loadData();
    loadTrashedBookings();
    loadTrashedEmails();
  }, [navigate, loadData, loadTrashedBookings, loadTrashedEmails]);

  // Load tab-specific data
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    if (activeTab === 'emails') {
      Promise.all([getSentEmails(), getReceivedEmails()])
        .then(([sentRes, receivedRes]) => {
          setSentEmails(sentRes.data.emails);
          setReceivedEmails(receivedRes.data.emails);
        }).catch(() => toast.error('Failed to load emails'));
    } else if (activeTab === 'analytics') {
      getSalesAnalytics()
        .then(res => setSalesData(res.data))
        .catch(() => toast.error('Failed to load analytics'));
    } else if (activeTab === 'traffic') {
      getTrafficAnalytics()
        .then(res => setTrafficData(res.data))
        .catch(() => toast.error('Failed to load traffic data'));
    } else if (activeTab === 'trash') {
      loadTrashedBookings();
      loadTrashedEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin');
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConfirmZellePayment = async (bookingId) => {
    try {
      // Update both payment_status to paid and status to confirmed
      await updateBookingStatus(bookingId, 'confirmed');
      // Also update payment status via API
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/confirm-zelle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Zelle payment confirmed! Receipt sent to customer.');
      loadData();
    } catch (error) {
      toast.error('Failed to confirm Zelle payment');
    }
  };

  const handleDownloadReceipt = async (bookingId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/receipt`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download receipt');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlatinumTouch_Receipt_${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const handleCreateBooking = async () => {
    setIsCreatingBooking(true);
    try {
      await adminCreateBooking(addBookingData);
      toast.success('Booking created successfully');
      setIsAddBookingOpen(false);
      setAddBookingData({
        service_type: 'round_trip',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        vehicle_year: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_color: '',
        lakewood_street: '',
        lakewood_city: 'Lakewood',
        lakewood_state: 'NJ',
        lakewood_zip: '08701',
        orlando_street: '',
        orlando_city: 'Orlando',
        orlando_state: 'FL',
        orlando_zip: '32801',
        payment_method: 'stripe',
        payment_status: 'pending',
        status: 'pending',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const openEmailDialog = (booking) => {
    setEmailTo(booking.customer.email);
    setEmailSubject(`Update on Your ${booking.service_name} Booking`);
    setEmailContent(`Dear ${booking.customer.full_name},\n\nThis is an update regarding your vehicle transport booking.\n\nBooking Details:\n- Service: ${booking.service_name}\n- Vehicle: ${booking.vehicle.year} ${booking.vehicle.make} ${booking.vehicle.model}\n\nPlease let us know if you have any questions.\n\nBest regards,\nPlatinum Touch Team`);
    setIsEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      const htmlContent = emailContent.replace(/\n/g, '<br>');
      const wrappedHtml = `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${htmlContent}</div>`;
      await sendEmail(emailTo, emailSubject, wrappedHtml, emailAttachments);
      toast.success('Email sent successfully');
      setIsEmailDialogOpen(false);
      setEmailAttachments([]);
      if (activeTab === 'emails') {
        const res = await getSentEmails();
        setSentEmails(res.data.emails);
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleMarkRead = async (emailId) => {
    try {
      await markEmailRead(emailId);
      setReceivedEmails(prev => prev.map(e => e.email_id === emailId ? { ...e, read: true } : e));
    } catch (error) {
      // silent
    }
  };

  const handleToggleReadUnread = async (email) => {
    try {
      if (email.read) {
        await markEmailUnread(email.email_id);
        setReceivedEmails(prev => prev.map(e => e.email_id === email.email_id ? { ...e, read: false } : e));
      } else {
        await markEmailRead(email.email_id);
        setReceivedEmails(prev => prev.map(e => e.email_id === email.email_id ? { ...e, read: true } : e));
      }
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleToggleStar = async (email, type) => {
    try {
      const fn = type === 'received' ? toggleStarReceived : toggleStarSent;
      const res = await fn(email.email_id);
      const newVal = res.data.starred;
      const setter = type === 'received' ? setReceivedEmails : setSentEmails;
      setter(prev => prev.map(e => e.email_id === email.email_id ? { ...e, starred: newVal } : e));
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleDeleteEmail = async (email, type) => {
    try {
      const fn = type === 'received' ? deleteReceivedEmail : deleteSentEmail;
      await fn(email.email_id);
      const setter = type === 'received' ? setReceivedEmails : setSentEmails;
      setter(prev => prev.filter(e => e.email_id !== email.email_id));
      toast.success('Email deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedEmailIds.size === 0) return;
    try {
      const ids = Array.from(selectedEmailIds);
      if (emailSubTab === 'received') {
        await bulkDeleteReceived(ids);
        setReceivedEmails(prev => prev.filter(e => !selectedEmailIds.has(e.email_id)));
      } else {
        await bulkDeleteSent(ids);
        setSentEmails(prev => prev.filter(e => !selectedEmailIds.has(e.email_id)));
      }
      setSelectedEmailIds(new Set());
      toast.success(`${ids.length} email${ids.length > 1 ? 's' : ''} deleted`);
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleBulkMarkRead = async () => {
    if (selectedEmailIds.size === 0 || emailSubTab !== 'received') return;
    try {
      const ids = Array.from(selectedEmailIds);
      await bulkMarkRead(ids);
      setReceivedEmails(prev => prev.map(e => selectedEmailIds.has(e.email_id) ? { ...e, read: true } : e));
      setSelectedEmailIds(new Set());
      toast.success('Marked as read');
    } catch (error) { toast.error('Failed to update'); }
  };

  const handleForwardEmail = (email) => {
    setEmailTo('');
    setEmailSubject(`Fwd: ${email.subject || ''}`);
    const fwdBody = email.text || '';
    setEmailContent(`\n\n--- Forwarded Message ---\nFrom: ${email.from || email.to || ''}\nSubject: ${email.subject || ''}\n\n${fwdBody}`);
    setEmailAttachments([]);
    setSelectedEmail(null);
    setIsEmailDialogOpen(true);
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmailIds(prev => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId);
      else next.add(emailId);
      return next;
    });
  };

  const toggleSelectAll = (emails) => {
    const ids = emails.map(e => e.email_id).filter(Boolean);
    const allSelected = ids.every(id => selectedEmailIds.has(id));
    if (allSelected) {
      setSelectedEmailIds(new Set());
    } else {
      setSelectedEmailIds(new Set(ids));
    }
  };

  const HighlightText = ({ text, search }) => {
    if (!search || !text) return <>{text}</>;
    const parts = text.split(new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return <>{parts.map((part, i) =>
      part.toLowerCase() === search.toLowerCase()
        ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>
        : part
    )}</>;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortValue = (booking, key) => {
    switch (key) {
      case 'customer': return booking.customer.full_name.toLowerCase();
      case 'email': return booking.customer.email.toLowerCase();
      case 'service': return booking.service_name.toLowerCase();
      case 'price': return booking.price;
      case 'vehicle': return `${booking.vehicle.year} ${booking.vehicle.make} ${booking.vehicle.model}`.toLowerCase();
      case 'payment': return booking.payment_status;
      case 'status': return booking.status;
      case 'created_at': return booking.created_at;
      default: return '';
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-[#38B6FF]" />
      : <ArrowDown className="w-3.5 h-3.5 text-[#38B6FF]" />;
  };

  const filteredBookings = bookings
    .filter(booking => 
      booking.customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.id.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);
      const mult = sortConfig.direction === 'asc' ? 1 : -1;
      if (typeof aVal === 'number') return (aVal - bVal) * mult;
      return String(aVal).localeCompare(String(bVal)) * mult;
    });

  const unreadCount = receivedEmails.filter(e => !e.read).length;

  const emailMatchesSearch = (email, term) => {
    if (!term) return true;
    const q = term.toLowerCase();
    const from = (email.from || '').toLowerCase();
    const to = (Array.isArray(email.to) ? email.to.join(' ') : email.to || '').toLowerCase();
    const subject = (email.subject || '').toLowerCase();
    const text = (email.text || '').toLowerCase();
    const hasAttachmentMatch = q === 'attachment' || q === 'attachments'
      ? (email.attachments?.length > 0)
      : false;
    const attachmentNames = (email.attachments || []).map(a => (a.filename || '').toLowerCase()).join(' ');
    return from.includes(q) || to.includes(q) || subject.includes(q) || text.includes(q) || attachmentNames.includes(q) || hasAttachmentMatch;
  };

  const filteredReceivedEmails = receivedEmails.filter(e => emailMatchesSearch(e, emailSearchTerm));
  const filteredSentEmails = sentEmails.filter(e => emailMatchesSearch(e, emailSearchTerm));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-lg text-slate-900 font-['Outfit']">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-slate-600 hover:text-[#38B6FF] transition-colors" data-testid="view-site-link">
                View Site
              </Link>
              <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#38B6FF] text-[#38B6FF]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'emails' && unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Dynamic Stats Cards - Changes based on active tab */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-section">
          {activeTab === 'bookings' && stats && (
            <>
              <StatCard icon={Users} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Total Bookings" value={stats.total_bookings} />
              <StatCard icon={Clock} iconBg="bg-yellow-100" iconColor="text-yellow-600" label="Pending" value={stats.pending_bookings} />
              <StatCard icon={CheckCircle} iconBg="bg-green-100" iconColor="text-green-600" label="Confirmed" value={stats.confirmed_bookings} />
              <StatCard icon={DollarSign} iconBg="bg-emerald-100" iconColor="text-emerald-600" label="Total Revenue" value={`$${stats.total_revenue.toLocaleString()}`} />
            </>
          )}
          {activeTab === 'emails' && (
            <>
              <StatCard icon={Inbox} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Total Received" value={receivedEmails.length} />
              <StatCard icon={Mail} iconBg="bg-red-100" iconColor="text-red-600" label="Unread" value={receivedEmails.filter(e => !e.read).length} />
              <StatCard icon={SendHorizontal} iconBg="bg-green-100" iconColor="text-green-600" label="Total Sent" value={sentEmails.length} />
              <StatCard icon={Star} iconBg="bg-yellow-100" iconColor="text-yellow-600" label="Starred" value={[...receivedEmails, ...sentEmails].filter(e => e.starred).length} />
            </>
          )}
          {activeTab === 'analytics' && salesData && (
            <>
              <StatCard icon={DollarSign} iconBg="bg-emerald-100" iconColor="text-emerald-600" label="Total Revenue" value={`$${salesData.total_revenue?.toLocaleString() || 0}`} />
              <StatCard icon={Users} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Total Orders" value={salesData.total_bookings || 0} />
              <StatCard icon={TrendingUp} iconBg="bg-green-100" iconColor="text-green-600" label="Avg. Order" value={`$${salesData.total_bookings ? Math.round(salesData.total_revenue / salesData.total_bookings) : 0}`} />
              <StatCard icon={CheckCircle} iconBg="bg-purple-100" iconColor="text-purple-600" label="Paid Orders" value={salesData.paid_bookings || 0} />
            </>
          )}
          {activeTab === 'traffic' && trafficData && (
            <>
              <StatCard icon={Globe} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Total Views" value={trafficData.total_views || 0} />
              <StatCard icon={Users} iconBg="bg-green-100" iconColor="text-green-600" label="Unique Visitors" value={trafficData.unique_visitors || 0} />
              <StatCard icon={Clock} iconBg="bg-yellow-100" iconColor="text-yellow-600" label="Avg. Session" value={`${Math.round(trafficData.avg_session_duration || 0)}s`} />
              <StatCard icon={TrendingUp} iconBg="bg-purple-100" iconColor="text-purple-600" label="Bounce Rate" value={`${trafficData.bounce_rate || 0}%`} />
            </>
          )}
          {activeTab === 'trash' && (
            <>
              <StatCard icon={Trash} iconBg="bg-red-100" iconColor="text-red-600" label="Trashed Bookings" value={trashedBookings.length} />
              <StatCard icon={Mail} iconBg="bg-orange-100" iconColor="text-orange-600" label="Trashed Emails" value={trashedEmails.length} />
              <StatCard icon={AlertTriangle} iconBg="bg-yellow-100" iconColor="text-yellow-600" label="Total Trashed" value={trashedBookings.length + trashedEmails.length} />
              <StatCard icon={ArchiveRestore} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Recoverable" value={trashedBookings.length + trashedEmails.length} />
            </>
          )}
        </div>

        {/* ========== BOOKINGS TAB ========== */}
        {activeTab === 'bookings' && (
          <>
            {/* Bookings Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200" data-testid="bookings-table-section">
              <div className="p-6 border-b border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h2 className="text-xl font-bold text-slate-900 font-['Outfit']">Bookings</h2>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Search bookings..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" data-testid="search-bookings-input" />
                    </div>
                    <Button onClick={() => setIsAddBookingOpen(true)} className="bg-[#38B6FF] hover:bg-blue-500 text-white flex items-center gap-2" data-testid="add-booking-btn">
                      <Plus className="w-4 h-4" /> Add Booking
                    </Button>
                    <Button variant="outline" onClick={() => withSpin('bookings', loadData)} className="flex items-center gap-2" data-testid="refresh-btn">
                      <RefreshCw className={`w-4 h-4 ${refreshingTab === 'bookings' ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="p-12 text-center text-slate-500">Loading bookings...</div>
              ) : filteredBookings.length === 0 ? (
                <div className="p-12 text-center text-slate-500">No bookings found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('customer')} data-testid="sort-customer">
                          <span className="flex items-center gap-1.5">Customer <SortIcon columnKey="customer" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('service')} data-testid="sort-service">
                          <span className="flex items-center gap-1.5">Service <SortIcon columnKey="service" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('price')} data-testid="sort-price">
                          <span className="flex items-center gap-1.5">Price <SortIcon columnKey="price" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('vehicle')} data-testid="sort-vehicle">
                          <span className="flex items-center gap-1.5">Vehicle <SortIcon columnKey="vehicle" /></span>
                        </TableHead>
                        {/* New Referral Column */}
                        <TableHead className="font-semibold text-slate-700 text-sm">
                          Referral
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('payment')} data-testid="sort-payment">
                          <span className="flex items-center gap-1.5">Payment <SortIcon columnKey="payment" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('status')} data-testid="sort-status">
                          <span className="flex items-center gap-1.5">Status <SortIcon columnKey="status" /></span>
                        </TableHead>
                        <TableHead className="cursor-pointer select-none hover:text-[#38B6FF] transition-colors" onClick={() => handleSort('created_at')} data-testid="sort-date">
                          <span className="flex items-center gap-1.5">Date <SortIcon columnKey="created_at" /></span>
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.map((booking) => (
                        <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                          <TableCell>
                            <p className="font-medium text-slate-900">{booking.customer.full_name}</p>
                            <p className="text-sm text-slate-500">{booking.customer.email}</p>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium text-slate-900">{booking.service_name}</p>
                          </TableCell>
                          <TableCell>
                            <p className="font-semibold text-slate-900">${booking.price.toLocaleString()}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-slate-900">{booking.vehicle.year} {booking.vehicle.make}</p>
                            <p className="text-sm text-slate-500">{booking.vehicle.model}</p>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 truncate max-w-[120px]" title={booking.referral_source}>
                              {booking.referral_source || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={PAYMENT_STATUS_COLORS[booking.payment_status] || 'bg-slate-100'}>{booking.payment_status}</Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-auto p-0">
                                  <Badge className={`${STATUS_COLORS[booking.status] || 'bg-slate-100'} cursor-pointer`}>
                                    {booking.status}<ChevronDown className="w-3 h-3 ml-1" />
                                  </Badge>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                {['pending','confirmed','in_transit','completed','cancelled'].map(s => (
                                  <DropdownMenuItem key={s} onClick={() => handleStatusChange(booking.id, s)}>{s.replace('_',' ')}</DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-slate-500">{new Date(booking.created_at).toLocaleDateString()}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Confirm Zelle Payment Button - only for tentative bookings */}
                              {booking.status === 'tentative' && booking.payment_method === 'zelle' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700"
                                  onClick={() => handleConfirmZellePayment(booking.id)}
                                  data-testid={`confirm-zelle-${booking.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Confirm Zelle
                                </Button>
                              )}
                              {/* Download Receipt Button */}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDownloadReceipt(booking.id)}
                                title="Download Receipt"
                                data-testid={`download-receipt-${booking.id}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setSelectedBooking(booking)} data-testid={`view-booking-${booking.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEmailDialog(booking)} data-testid={`email-booking-${booking.id}`}>
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirmBooking(booking)} data-testid={`delete-booking-${booking.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ========== EMAILS TAB ========== */}
        {activeTab === 'emails' && (
          <div data-testid="emails-section">
            {/* Email Sub-tabs + Search + Actions */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button
                variant={emailSubTab === 'received' ? 'default' : 'outline'}
                onClick={() => { setEmailSubTab('received'); setSelectedEmailIds(new Set()); }}
                className={emailSubTab === 'received' ? 'bg-[#38B6FF] hover:bg-blue-500 text-white' : ''}
                data-testid="emails-received-tab"
              >
                <Inbox className="w-4 h-4 mr-2" />
                Inbox {unreadCount > 0 && `(${unreadCount})`}
              </Button>
              <Button
                variant={emailSubTab === 'sent' ? 'default' : 'outline'}
                onClick={() => { setEmailSubTab('sent'); setSelectedEmailIds(new Set()); }}
                className={emailSubTab === 'sent' ? 'bg-[#38B6FF] hover:bg-blue-500 text-white' : ''}
                data-testid="emails-sent-tab"
              >
                <SendHorizontal className="w-4 h-4 mr-2" />
                Sent ({sentEmails.length})
              </Button>
              <div className="relative ml-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search emails, addresses, attachments..."
                  value={emailSearchTerm}
                  onChange={(e) => setEmailSearchTerm(e.target.value)}
                  className="pl-9 w-72"
                  data-testid="search-emails-input"
                />
              </div>
              {emailSearchTerm && (
                <span className="text-xs text-slate-500">
                  {emailSubTab === 'received' ? filteredReceivedEmails.length : filteredSentEmails.length} result{(emailSubTab === 'received' ? filteredReceivedEmails.length : filteredSentEmails.length) !== 1 ? 's' : ''}
                </span>
              )}
              <div className="flex-1" />
              <Button
                onClick={() => {
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailContent('');
                  setEmailAttachments([]);
                  setIsEmailDialogOpen(true);
                }}
                className="bg-[#38B6FF] hover:bg-blue-500 text-white"
                data-testid="compose-email-btn"
              >
                <PenSquare className="w-4 h-4 mr-2" /> Compose
              </Button>
              <Button variant="outline" onClick={() => withSpin('emails', async () => {
                const [s, r] = await Promise.all([getSentEmails(), getReceivedEmails()]);
                setSentEmails(s.data.emails); setReceivedEmails(r.data.emails);
              })} data-testid="refresh-emails-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingTab === 'emails' ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Bulk Actions Bar */}
            {selectedEmailIds.size > 0 && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="bulk-actions-bar">
                <span className="text-sm font-medium text-blue-700">{selectedEmailIds.size} selected</span>
                <div className="flex-1" />
                {emailSubTab === 'received' && (
                  <Button variant="outline" size="sm" onClick={handleBulkMarkRead} data-testid="bulk-mark-read-btn">
                    <MailCheck className="w-4 h-4 mr-1" /> Mark Read
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleBulkDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedEmailIds(new Set())} data-testid="clear-selection-btn">
                  Clear
                </Button>
              </div>
            )}

            {/* Received Emails */}
            {emailSubTab === 'received' && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <button
                    onClick={() => toggleSelectAll(filteredReceivedEmails)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="select-all-received"
                  >
                    {filteredReceivedEmails.length > 0 && filteredReceivedEmails.every(e => selectedEmailIds.has(e.email_id))
                      ? <CheckSquare className="w-5 h-5 text-[#38B6FF]" />
                      : <Square className="w-5 h-5" />}
                  </button>
                  <h2 className="text-lg font-bold text-slate-900 font-['Outfit']">Inbox</h2>
                  <span className="text-sm text-slate-400">({filteredReceivedEmails.length})</span>
                </div>
                {filteredReceivedEmails.length === 0 ? (
                  <div className="p-12 text-center">
                    <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">{emailSearchTerm ? 'No emails match your search' : 'No incoming emails yet'}</p>
                    <p className="text-sm text-slate-400 mt-1">{emailSearchTerm ? 'Try a different search term' : 'Emails sent to @platinumtouchnj.com will appear here'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredReceivedEmails.map((email) => (
                      <div
                        key={email.email_id || Math.random().toString()}
                        className={`group p-3 px-4 hover:bg-slate-50 transition-colors ${!email.read ? 'bg-blue-50/40' : ''} ${selectedEmailIds.has(email.email_id) ? 'bg-blue-50' : ''}`}
                        data-testid={`received-email-${email.email_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleEmailSelection(email.email_id); }}
                            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                            data-testid={`select-email-${idx}`}
                          >
                            {selectedEmailIds.has(email.email_id)
                              ? <CheckSquare className="w-4 h-4 text-[#38B6FF]" />
                              : <Square className="w-4 h-4" />}
                          </button>
                          {/* Star */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleStar(email, 'received'); }}
                            className="flex-shrink-0"
                            data-testid={`star-email-${idx}`}
                          >
                            <Star className={`w-4 h-4 ${email.starred ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300 hover:text-yellow-400'}`} />
                          </button>
                          {/* Unread dot */}
                          <div className="w-2 flex-shrink-0">
                            {!email.read && <div className="w-2 h-2 rounded-full bg-[#38B6FF]" />}
                          </div>
                          {/* Content - clickable */}
                          <div
                            className="min-w-0 flex-1 cursor-pointer"
                            onClick={() => { setSelectedEmail(email); if (!email.read && email.email_id) handleMarkRead(email.email_id); }}
                          >
                            <div className="flex items-center gap-2">
                              <p className={`text-sm truncate ${!email.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                                <HighlightText text={email.from || 'Unknown sender'} search={emailSearchTerm} />
                              </p>
                            </div>
                            <p className={`text-sm truncate mt-0.5 ${!email.read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                              <HighlightText text={email.subject || '(No Subject)'} search={emailSearchTerm} />
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-slate-400 truncate">
                                <HighlightText text={email.text ? email.text.substring(0, 100) : ''} search={emailSearchTerm} />
                              </p>
                              {email.attachments?.length > 0 && (
                                <span className="flex items-center gap-0.5 text-xs text-slate-400 flex-shrink-0">
                                  <Paperclip className="w-3 h-3" /> {email.attachments.length}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Date */}
                          <p className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                            {email.received_at ? new Date(email.received_at).toLocaleString() : ''}
                          </p>
                          {/* Quick Actions */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); handleToggleReadUnread(email); }} className="p-1 rounded hover:bg-slate-200" title={email.read ? 'Mark unread' : 'Mark read'} data-testid={`toggle-read-${idx}`}>
                              {email.read ? <MailOpen className="w-4 h-4 text-slate-500" /> : <MailCheck className="w-4 h-4 text-slate-500" />}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteEmail(email, 'received'); }} className="p-1 rounded hover:bg-red-100" title="Delete" data-testid={`delete-email-${idx}`}>
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sent Emails */}
            {emailSubTab === 'sent' && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <button
                    onClick={() => toggleSelectAll(filteredSentEmails)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="select-all-sent"
                  >
                    {filteredSentEmails.length > 0 && filteredSentEmails.every(e => selectedEmailIds.has(e.email_id))
                      ? <CheckSquare className="w-5 h-5 text-[#38B6FF]" />
                      : <Square className="w-5 h-5" />}
                  </button>
                  <h2 className="text-lg font-bold text-slate-900 font-['Outfit']">Sent Emails</h2>
                  <span className="text-sm text-slate-400">({filteredSentEmails.length})</span>
                </div>
                {filteredSentEmails.length === 0 ? (
                  <div className="p-12 text-center">
                    <SendHorizontal className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">{emailSearchTerm ? 'No emails match your search' : 'No emails sent yet'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredSentEmails.map((email) => (
                      <div
                        key={email.email_id || Math.random().toString()}
                        className={`group p-3 px-4 hover:bg-slate-50 transition-colors ${selectedEmailIds.has(email.email_id) ? 'bg-blue-50' : ''}`}
                        data-testid={`sent-email-${email.email_id}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleEmailSelection(email.email_id); }}
                            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                          >
                            {selectedEmailIds.has(email.email_id)
                              ? <CheckSquare className="w-4 h-4 text-[#38B6FF]" />
                              : <Square className="w-4 h-4" />}
                          </button>
                          {/* Star */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleStar(email, 'sent'); }}
                            className="flex-shrink-0"
                          >
                            <Star className={`w-4 h-4 ${email.starred ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300 hover:text-yellow-400'}`} />
                          </button>
                          {/* Content - clickable */}
                          <div
                            className="min-w-0 flex-1 cursor-pointer"
                            onClick={() => setSelectedEmail(email)}
                          >
                            <p className="text-sm text-slate-700">
                              To: <span className="font-medium text-slate-900"><HighlightText text={email.to} search={emailSearchTerm} /></span>
                            </p>
                            <p className="text-sm font-medium text-slate-800 truncate mt-0.5">
                              <HighlightText text={email.subject} search={emailSearchTerm} />
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="text-xs" variant="outline">
                                {email.type === 'booking_confirmation' ? 'Booking Confirmation' : 'Manual'}
                              </Badge>
                              {email.attachments?.length > 0 && (
                                <span className="flex items-center gap-0.5 text-xs text-slate-400">
                                  <Paperclip className="w-3 h-3" /> {email.attachments.length} file{email.attachments.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Date */}
                          <p className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                            {email.sent_at ? new Date(email.sent_at).toLocaleString() : ''}
                          </p>
                          {/* Quick Actions */}
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteEmail(email, 'sent'); }} className="p-1 rounded hover:bg-red-100" title="Delete">
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ========== SALES ANALYTICS TAB ========== */}
        {activeTab === 'analytics' && (
          <div data-testid="analytics-section">
            {/* Summary Cards */}
            {salesData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <StatCard icon={DollarSign} iconBg="bg-emerald-100" iconColor="text-emerald-600" label="Total Revenue (Paid)" value={`$${salesData.total_revenue?.toLocaleString() || 0}`} />
                <StatCard icon={CheckCircle} iconBg="bg-blue-100" iconColor="text-[#38B6FF]" label="Total Paid Bookings" value={salesData.total_paid || 0} />
              </div>
            )}

            {/* Period Toggle */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm font-medium text-slate-600">View by:</span>
              {['hourly','daily','weekly'].map(p => (
                <Button
                  key={p}
                  variant={salesPeriod === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSalesPeriod(p)}
                  className={salesPeriod === p ? 'bg-[#38B6FF] hover:bg-blue-500 text-white' : ''}
                  data-testid={`sales-period-${p}`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={() => withSpin('analytics', async () => {
                const r = await getSalesAnalytics(); setSalesData(r.data);
              })}>
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingTab === 'analytics' ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Revenue Chart */}
            {salesData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                    <TrendingUp className="w-5 h-5 inline mr-2 text-[#38B6FF]" />
                    Revenue ({salesPeriod})
                  </h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesData[salesPeriod] || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
                        <Bar dataKey="revenue" fill="#38B6FF" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                    <Users className="w-5 h-5 inline mr-2 text-emerald-500" />
                    Bookings ({salesPeriod})
                  </h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={salesData[salesPeriod] || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Service Breakdown */}
            {salesData?.service_breakdown && Object.keys(salesData.service_breakdown).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">Service Breakdown</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead>Bookings</TableHead>
                        <TableHead>Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(salesData.service_breakdown).map(([key, val]) => (
                        <TableRow key={key}>
                          <TableCell className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                          <TableCell>{val.count}</TableCell>
                          <TableCell className="font-semibold text-emerald-600">${val.revenue.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== TRAFFIC TAB ========== */}
        {activeTab === 'traffic' && (
          <div data-testid="traffic-section">
            {/* Summary Cards */}
            {trafficData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard icon={Eye} iconBg="bg-purple-100" iconColor="text-purple-600" label="Total Page Views" value={trafficData.total_views || 0} />
                <StatCard icon={Users} iconBg="bg-indigo-100" iconColor="text-indigo-600" label="Unique Visitors" value={trafficData.total_unique || 0} />
                <StatCard icon={Timer} iconBg="bg-amber-100" iconColor="text-amber-600" label="Avg. Time on Site" value={formatDuration(trafficData.avg_time_on_site || 0)} />
                <StatCard icon={Globe} iconBg="bg-teal-100" iconColor="text-teal-600" label="Total Sessions" value={trafficData.total_sessions || 0} />
              </div>
            )}

            {/* Period Toggle */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm font-medium text-slate-600">View by:</span>
              {['hourly','daily','weekly','monthly'].map(p => (
                <Button
                  key={p}
                  variant={trafficPeriod === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrafficPeriod(p)}
                  className={trafficPeriod === p ? 'bg-[#38B6FF] hover:bg-blue-500 text-white' : ''}
                  data-testid={`traffic-period-${p}`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={() => withSpin('traffic', async () => {
                const r = await getTrafficAnalytics(); setTrafficData(r.data);
              })}>
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingTab === 'traffic' ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Traffic Charts */}
            {trafficData && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                      <Eye className="w-5 h-5 inline mr-2 text-purple-500" />
                      Page Views ({trafficPeriod})
                    </h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trafficData[trafficPeriod] || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="views" fill="#8b5cf6" name="Page Views" radius={[4,4,0,0]} />
                          <Bar dataKey="unique" fill="#38B6FF" name="Unique Visitors" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                      <Timer className="w-5 h-5 inline mr-2 text-amber-500" />
                      Avg. Time on Site ({trafficPeriod})
                    </h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trafficData[trafficPeriod] || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${Math.round(v)}s`} />
                          <Tooltip formatter={(v) => [`${formatDuration(v)}`, 'Avg. Time']} />
                          <Line type="monotone" dataKey="avg_time" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Avg Time (s)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                    <Globe className="w-5 h-5 inline mr-2 text-indigo-500" />
                    Top Pages
                  </h3>
                  {trafficData.top_pages?.length > 0 ? (
                    <div className="space-y-3">
                      {trafficData.top_pages.map((p, i) => (
                        <div key={`page-${p.page}`} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-slate-400 w-5">#{i + 1}</span>
                            <span className="text-sm font-medium text-slate-700">{p.page}</span>
                          </div>
                          <span className="text-sm font-semibold text-slate-900">{p.views} views</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">No page views recorded yet</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== TRASH TAB ========== */}
        {activeTab === 'trash' && (
          <div data-testid="transport-trash-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 font-['Outfit']">Trash</h2>
                <p className="text-sm text-slate-500">Deleted bookings are kept for 30 days before permanent deletion</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => withSpin('trash', loadTrashedBookings)} data-testid="transport-trash-refresh">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingTab === 'trash' ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {trashedBookings.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
                <Trash className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-400">Trash is empty</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Deleted</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trashedBookings.map(b => {
                      const deletedAt = new Date(b.deleted_at);
                      const expiresAt = new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
                      const daysLeft = Math.ceil((expiresAt - new Date()) / (24 * 60 * 60 * 1000));
                      return (
                        <TableRow key={b.id}>
                          <TableCell>
                            <p className="font-medium text-slate-900 text-sm">{b.customer?.full_name}</p>
                            <p className="text-xs text-slate-500">{b.customer?.email}</p>
                          </TableCell>
                          <TableCell className="text-sm">{b.service_name}</TableCell>
                          <TableCell className="text-sm font-medium">${b.price}</TableCell>
                          <TableCell className="text-sm text-slate-500">{deletedAt.toLocaleDateString()}</TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium ${daysLeft <= 7 ? 'text-red-500' : 'text-slate-500'}`}>
                              {daysLeft} days left
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => handleRestoreBooking(b.id)} data-testid={`restore-transport-booking-${b.id}`}>
                                <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handlePermanentDelete(b.id)} data-testid={`permanent-delete-transport-${b.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Trashed Emails Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Deleted Emails ({trashedEmails.length})
              </h3>
              {trashedEmails.length === 0 ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl py-8 text-center">
                  <p className="text-slate-400">No deleted emails</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>From/To</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Deleted</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trashedEmails.map(e => {
                        const deletedAt = new Date(e.deleted_at);
                        const expiresAt = new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
                        const daysLeft = Math.ceil((expiresAt - new Date()) / (24 * 60 * 60 * 1000));
                        return (
                          <TableRow key={e.email_id}>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${e.type === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                {e.type === 'sent' ? 'Sent' : 'Received'}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">
                              {e.type === 'sent' ? e.to : e.from}
                            </TableCell>
                            <TableCell className="text-sm truncate max-w-xs">{e.subject}</TableCell>
                            <TableCell className="text-sm text-slate-500">{deletedAt.toLocaleDateString()}</TableCell>
                            <TableCell>
                              <span className={`text-xs font-medium ${daysLeft <= 7 ? 'text-red-500' : 'text-slate-500'}`}>
                                {daysLeft} days left
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleRestoreEmail(e.email_id, e.type)} data-testid={`restore-transport-email-${e.email_id}`}>
                                  <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handlePermanentDeleteEmail(e.email_id, e.type)} data-testid={`permanent-delete-transport-email-${e.email_id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ========== DELETE CONFIRMATION MODAL ========== */}
      {deleteConfirmBooking && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirmBooking(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="font-bold text-xl text-slate-900 mb-2">Delete Booking?</h3>
              <p className="text-slate-500 mb-2">
                Are you sure you want to delete this booking for <strong>{deleteConfirmBooking.customer?.full_name}</strong>?
              </p>
              <p className="text-sm text-slate-400 mb-6">
                The booking will be moved to trash and kept for 30 days before permanent deletion.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => setDeleteConfirmBooking(null)} data-testid="transport-delete-cancel-btn">
                  Cancel
                </Button>
                <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDeleteBooking(deleteConfirmBooking)} data-testid="transport-delete-confirm-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Booking
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Booking Details</DialogTitle>
            <DialogDescription>View complete booking information</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Customer</h4>
                  <p className="text-slate-600">{selectedBooking.customer.full_name}</p>
                  <p className="text-slate-600">{selectedBooking.customer.email}</p>
                  <p className="text-slate-600">{selectedBooking.customer.phone}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Service</h4>
                  <p className="text-slate-600">{selectedBooking.service_name}</p>
                  <p className="text-lg font-bold text-[#38B6FF]">${selectedBooking.price.toLocaleString()}</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Vehicle</h4>
                <p className="text-slate-600">{selectedBooking.vehicle.year} {selectedBooking.vehicle.make} {selectedBooking.vehicle.model}</p>
                <p className="text-slate-500 text-sm">Color: {selectedBooking.vehicle.color}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Lakewood Address</h4>
                  <p className="text-slate-600">
                    {selectedBooking.lakewood_address.street}<br />
                    {selectedBooking.lakewood_address.city}, {selectedBooking.lakewood_address.state} {selectedBooking.lakewood_address.zip_code}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Orlando Address</h4>
                  <p className="text-slate-600">
                    {selectedBooking.orlando_address.street}<br />
                    {selectedBooking.orlando_address.city}, {selectedBooking.orlando_address.state} {selectedBooking.orlando_address.zip_code}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t">
                <Badge className={STATUS_COLORS[selectedBooking.status]}>Status: {selectedBooking.status}</Badge>
                <Badge className={PAYMENT_STATUS_COLORS[selectedBooking.payment_status]}>Payment: {selectedBooking.payment_status}</Badge>
              </div>
              <div className="text-sm text-slate-500">Booking ID: {selectedBooking.id}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Send Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={(open) => { setIsEmailDialogOpen(open); if (!open) setEmailAttachments([]); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Send Email</DialogTitle>
            <DialogDescription>Send an email with optional attachments</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="mt-1" data-testid="email-to-input" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="mt-1" data-testid="email-subject-input" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={emailContent} onChange={(e) => setEmailContent(e.target.value)} rows={6} className="mt-1" data-testid="email-content-input" />
            </div>
            {/* Attachments */}
            <div>
              <Label>Attachments</Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors text-sm text-slate-600" data-testid="attach-file-label">
                  <Paperclip className="w-4 h-4" />
                  Attach Files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="attach-file-input"
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files || []);
                      setEmailAttachments(prev => [...prev, ...newFiles]);
                      e.target.value = '';
                    }}
                  />
                </label>
                <span className="text-xs text-slate-400">{emailAttachments.length} file{emailAttachments.length !== 1 ? 's' : ''} attached</span>
              </div>
              {emailAttachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {emailAttachments.map((file, idx) => (
                    <div key={`att-${file.name}-${idx}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5 text-sm" data-testid={`attachment-chip-${idx}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate text-slate-700">{file.name}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        onClick={() => setEmailAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="text-slate-400 hover:text-red-500 ml-2 flex-shrink-0"
                        data-testid={`remove-attachment-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsEmailDialogOpen(false); setEmailAttachments([]); }}>Cancel</Button>
              <Button onClick={handleSendEmail} disabled={isSendingEmail} className="bg-[#38B6FF] hover:bg-blue-500 text-white" data-testid="send-email-btn">
                {isSendingEmail ? 'Sending...' : 'Send Email'}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email View Dialog */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {selectedEmail?.subject || '(No Subject)'}
            </DialogTitle>
            <DialogDescription>
              {selectedEmail?.from ? `From: ${selectedEmail.from}` : selectedEmail?.to ? `To: ${selectedEmail.to}` : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-slate-500 border-b pb-3">
                {selectedEmail.from && <span>From: <strong className="text-slate-700">{selectedEmail.from}</strong></span>}
                {selectedEmail.to && <span>To: <strong className="text-slate-700">{Array.isArray(selectedEmail.to) ? selectedEmail.to.join(', ') : selectedEmail.to}</strong></span>}
                <span>{selectedEmail.sent_at ? new Date(selectedEmail.sent_at).toLocaleString() : selectedEmail.received_at ? new Date(selectedEmail.received_at).toLocaleString() : ''}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                {selectedEmail.html ? (
                  <div className="text-sm text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.html) }} />
                ) : (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedEmail.text || 'No content'}</p>
                )}
              </div>
              {/* Attachments display */}
              {selectedEmail.attachments?.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                    <Paperclip className="w-4 h-4" />
                    Attachments ({selectedEmail.attachments.length})
                  </p>
                  <div className="space-y-1.5">
                    {selectedEmail.attachments.map((att, idx) => (
                      <div key={`s-att-${att.filename}-${idx}`} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`email-attachment-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span className="truncate text-slate-700 font-medium">{att.filename}</span>
                          {att.size > 0 && <span className="text-xs text-slate-400 flex-shrink-0">({(att.size / 1024).toFixed(1)} KB)</span>}
                        </div>
                        {att.file_id ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${att.file_id}?token=${localStorage.getItem('admin_token')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[#38B6FF] hover:text-blue-600 text-xs font-medium ml-2 flex-shrink-0"
                            data-testid={`download-attachment-${idx}`}
                          >
                            <Download className="w-3.5 h-3.5" /> Download
                          </a>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                toast.info('Retrying attachment download...');
                                const res = await retryAttachments(selectedEmail.email_id);
                                if (res.data.updated) {
                                  toast.success('Attachment downloaded successfully');
                                  setSelectedEmail(prev => ({ ...prev, attachments: res.data.attachments }));
                                  setReceivedEmails(prev => prev.map(e => 
                                    e.email_id === selectedEmail.email_id ? { ...e, attachments: res.data.attachments } : e
                                  ));
                                } else {
                                  toast.error('Could not download attachment. It may have expired.');
                                }
                              } catch (err) {
                                toast.error('Failed to retry attachment download');
                              }
                            }}
                            className="flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs font-medium ml-2 flex-shrink-0"
                            data-testid={`retry-attachment-${idx}`}
                          >
                            <RotateCw className="w-3.5 h-3.5" /> Retry Download
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Reply/Forward buttons for received emails */}
              {selectedEmail.from && (
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleForwardEmail(selectedEmail)}
                    data-testid="forward-email-btn"
                  >
                    <Forward className="w-4 h-4 mr-2" /> Forward
                  </Button>
                  <Button
                    onClick={() => {
                      const replyTo = selectedEmail.from;
                      const replySubject = selectedEmail.subject?.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject || ''}`;
                      const originalText = selectedEmail.text || '';
                      const quotedText = originalText.split('\n').map(l => `> ${l}`).join('\n');
                      setEmailTo(replyTo);
                      setEmailSubject(replySubject);
                      setEmailContent(`\n\n--- Original Message ---\n${quotedText}`);
                      setEmailAttachments([]);
                      setSelectedEmail(null);
                      setIsEmailDialogOpen(true);
                    }}
                    className="bg-[#38B6FF] hover:bg-blue-500 text-white"
                    data-testid="reply-email-btn"
                  >
                    <Reply className="w-4 h-4 mr-2" /> Reply
                  </Button>
                </div>
              )}
              {/* Forward button for sent emails */}
              {!selectedEmail.from && selectedEmail.to && (
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleForwardEmail(selectedEmail)}
                    data-testid="forward-sent-email-btn"
                  >
                    <Forward className="w-4 h-4 mr-2" /> Forward
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Booking Dialog */}
      <Dialog open={isAddBookingOpen} onOpenChange={setIsAddBookingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Add New Booking</DialogTitle>
            <DialogDescription>Manually create a booking for a customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Service Type */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Service Type</Label>
              <select
                value={addBookingData.service_type}
                onChange={(e) => setAddBookingData(prev => ({ ...prev, service_type: e.target.value }))}
                className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
              >
                <option value="lakewood_to_orlando">Lakewood to Orlando - $1,500</option>
                <option value="orlando_to_lakewood">Orlando to Lakewood - $1,500</option>
                <option value="round_trip">Round Trip - $2,800</option>
              </select>
            </div>

            {/* Customer Info */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Customer Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Full Name</Label>
                  <Input
                    value={addBookingData.customer_name}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, customer_name: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Phone</Label>
                  <Input
                    value={addBookingData.customer_phone}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, customer_phone: e.target.value }))}
                    placeholder="732-555-1234"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-slate-600">Email</Label>
                  <Input
                    type="email"
                    value={addBookingData.customer_email}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Vehicle Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Year</Label>
                  <Input
                    value={addBookingData.vehicle_year}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, vehicle_year: e.target.value }))}
                    placeholder="2024"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Make</Label>
                  <Input
                    value={addBookingData.vehicle_make}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Model</Label>
                  <Input
                    value={addBookingData.vehicle_model}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                    placeholder="Camry"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Color</Label>
                  <Input
                    value={addBookingData.vehicle_color}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, vehicle_color: e.target.value }))}
                    placeholder="Black"
                  />
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Addresses</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 font-medium">Lakewood Address</Label>
                  <Input
                    value={addBookingData.lakewood_street}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, lakewood_street: e.target.value }))}
                    placeholder="123 Main St"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={addBookingData.lakewood_city}
                      onChange={(e) => setAddBookingData(prev => ({ ...prev, lakewood_city: e.target.value }))}
                      placeholder="City"
                      className="flex-1"
                    />
                    <Input
                      value={addBookingData.lakewood_zip}
                      onChange={(e) => setAddBookingData(prev => ({ ...prev, lakewood_zip: e.target.value }))}
                      placeholder="Zip"
                      className="w-24"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 font-medium">Orlando Address</Label>
                  <Input
                    value={addBookingData.orlando_street}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, orlando_street: e.target.value }))}
                    placeholder="456 Palm Dr"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={addBookingData.orlando_city}
                      onChange={(e) => setAddBookingData(prev => ({ ...prev, orlando_city: e.target.value }))}
                      placeholder="City"
                      className="flex-1"
                    />
                    <Input
                      value={addBookingData.orlando_zip}
                      onChange={(e) => setAddBookingData(prev => ({ ...prev, orlando_zip: e.target.value }))}
                      placeholder="Zip"
                      className="w-24"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment & Status */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Payment & Status</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Payment Method</Label>
                  <select
                    value={addBookingData.payment_method}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="stripe">Credit Card (Stripe)</option>
                    <option value="zelle">Zelle</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Payment Status</Label>
                  <select
                    value={addBookingData.payment_status}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, payment_status: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Booking Status</Label>
                  <select
                    value={addBookingData.status}
                    onChange={(e) => setAddBookingData(prev => ({ ...prev, status: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md"
                  >
                    <option value="pending">Pending</option>
                    <option value="tentative">Tentative</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm text-slate-600">Notes (Optional)</Label>
              <Textarea
                value={addBookingData.notes}
                onChange={(e) => setAddBookingData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setIsAddBookingOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateBooking} 
              disabled={isCreatingBooking}
              className="bg-[#38B6FF] hover:bg-blue-500 text-white"
            >
              {isCreatingBooking ? 'Creating...' : 'Create Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function StatCard({ icon: Icon, iconBg, iconColor, label, value }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 font-['Outfit']">{value}</p>
        </div>
      </div>
    </div>
  );
}
